import React, { useEffect, useState } from 'react'
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
	Home,
	MapPin,
	GitBranch,
	FolderOpen,
	ListTodo,
	Users,
	BookOpen,
	Archive,
	Wrench,
	ChevronDown,
	ChevronRight,
	X,
	Smile,
	LogOut,
	ClipboardCheck,
	LayoutTemplate,
	LifeBuoy,
	UserCheck,
	FileText,
	Building2,
	Table2,
	Presentation,
	PanelLeftClose,
	PanelLeftOpen,
	Handshake,
} from 'lucide-react'
import { logout } from 'slices/authSlice'
import routes from 'constants/routes'
import { isDesktopOnlyPath } from 'constants/desktopOnlyRoutes'
// featureFlag directly, NOT the features/Presentations barrel: the barrel pulls
// in PresentationsScreen, which imports ScreenShell, which imports this file.
import { isPresentationsEnabled } from 'features/Presentations/featureFlag'
import useIsMobile from 'hooks/useIsMobile'
import { usePersistedToggle, viewportAtLeast } from 'hooks/usePersistedToggle'
import AvatarLogoFull from 'images/MDT_FullLogo.png'
import FolderLogo from 'images/MDT_FolderLogo.png'

interface AdminPanelCNProps {
	mobileOpen?: boolean
	onMobileClose?: () => void
}

interface CollapsibleSectionProps {
	label: string
	icon: React.ReactNode
	children: React.ReactNode
	defaultOpen?: boolean
	pathPrefix?: string
}

const CollapsibleSection = ({ label, icon, children, defaultOpen = false, pathPrefix }: CollapsibleSectionProps) => {
	const location = useLocation()
	const matchesRoute = pathPrefix
		? pathPrefix.split(',').some(p => location.pathname.startsWith(p.trim()))
		: false
	const [open, setOpen] = useState(defaultOpen || matchesRoute)

	useEffect(() => {
		if (matchesRoute) setOpen(true)
	}, [matchesRoute])

	return (
		<div>
			<button
				type="button"
				onClick={() => setOpen(!open)}
				className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md mx-2 transition-colors w-[calc(100%-16px)] text-left"
			>
				<span className="w-4 h-4 flex-shrink-0 flex items-center justify-center">{icon}</span>
				<span className="flex-1">{label}</span>
				{open ? (
					<ChevronDown className="w-3 h-3 text-gray-400 transition-transform" />
				) : (
					<ChevronRight className="w-3 h-3 text-gray-400 transition-transform" />
				)}
			</button>
			{open && <div className="mt-0.5 space-y-0.5">{children}</div>}
		</div>
	)
}

const navLinkClass = (isActive: boolean) =>
	`flex items-center gap-3 px-4 py-2 text-sm rounded-md mx-2 transition-colors no-underline ${
		isActive ? 'text-[#0080fe] bg-highlight font-semibold' : 'text-gray-700 hover:bg-gray-100'
	}`

const subLinkClass = (isActive: boolean) =>
	`flex items-center gap-3 pl-11 pr-4 py-1.5 text-sm rounded-md mx-2 transition-colors no-underline ${
		isActive ? 'text-[#0080fe] bg-highlight font-semibold' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
	}`

const sectionLabelClass =
	'text-[10px] font-bold uppercase tracking-wider text-gray-400 px-4 pt-3 pb-1'

// Custom active-state matcher for module-level routes (Scoring, Financials,
// Risks, ProsCons, Approvals, Compare, Reports). The link should highlight
// when the user is on either:
//   - the global page  (e.g. /scoring, /scoring?eval=abc)
//   - the nested route (e.g. /evaluations/:id/scoring)
// per the A3 acceptance criterion: "on /evaluations/:id/scoring, both
// 'Evaluations' and 'Scoring' highlight."
function isModuleActive(moduleSlug: string, pathname: string): boolean {
	if (pathname === `/${moduleSlug}`) return true
	if (pathname.startsWith(`/${moduleSlug}/`)) return true
	if (pathname.startsWith(`/${moduleSlug}?`)) return true
	const segments = pathname.split('/').filter(Boolean)
	return segments[segments.length - 1] === moduleSlug
}

interface EvalSubItem {
	to: string
	slug: string
	icon: React.ReactNode
	label: string
}

// Evaluations sub-items — user-locked to THREE entries: Approvals,
// Evaluation Sites and Reports.
//
// Compare / Map / Scoring / Financials / Risks / Pros & Cons must NOT
// appear here even if a future Linear ticket prescribes adding them —
// those modules remain reachable only from inside an evaluation detail
// page (/evaluations/:id/<module>). That part of the 2026-04-30 directive
// still stands.
//
// "Evaluation Sites" was previously excluded from this group and lived
// under ADMIN/Archives. The user reversed that on 2026-08-25 and asked for
// it here, in line with Approvals. Do not move it back to Archives on the
// strength of the older note.
//
// "Reports" joined this group on 2026-09-04 at the user's direction,
// replacing its previous top-level ADMIN placement. A report is generated
// from evaluation data, so it belongs beside Approvals and Evaluation
// Sites. Do not move it back to ADMIN on the strength of the older note
// that used to sit in the Admin section.
const evalSubItems: EvalSubItem[] = [
	{ to: routes.APPROVALS, slug: 'approvals', icon: <UserCheck className="w-4 h-4" />, label: 'Approvals' },
	{
		to: routes.ALL_SITES,
		slug: 'evaluation-sites',
		icon: <Building2 className="w-4 h-4" />,
		label: 'Evaluation Sites',
	},
	{ to: routes.REPORTS, slug: 'reports', icon: <FileText className="w-4 h-4" />, label: 'Reports' },
]

interface EvaluationsNavGroupProps {
	onNav?: () => void
}

// Sidebar group with a clickable parent (navigates to /evaluations) plus a
// chevron that toggles its sub-items. Auto-expands whenever the user is on
// any /evaluations* path or any of the SSP-parity module routes.
const EvaluationsNavGroup = ({ onNav }: EvaluationsNavGroupProps) => {
	const location = useLocation()
	const inEvalContext =
		location.pathname.startsWith(routes.EVALUATIONS) ||
		evalSubItems.some(item => isModuleActive(item.slug, location.pathname))
	const [open, setOpen] = useState(inEvalContext)

	useEffect(() => {
		if (inEvalContext) setOpen(true)
	}, [inEvalContext])

	return (
		<div>
			{/* Parent row: NavLink for the label area, separate chevron toggle */}
			<div className="flex items-center mx-2 rounded-md hover:bg-gray-50 transition-colors">
				<NavLink
					to={routes.EVALUATIONS}
					end
					onClick={onNav}
					className={({ isActive }) =>
						`flex-1 flex items-center gap-3 pl-4 pr-2 py-2 text-sm rounded-l-md no-underline transition-colors ${
							isActive ? 'text-[#0080fe] font-semibold' : 'text-gray-700'
						}`
					}
					data-tour="nav-evaluations"
				>
					<span className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
						<ClipboardCheck className="w-5 h-5" />
					</span>
					<span>Evaluations</span>
				</NavLink>
				<button
					type="button"
					onClick={() => setOpen(!open)}
					aria-label={open ? 'Collapse Evaluations' : 'Expand Evaluations'}
					className="px-2 py-2 text-gray-400 hover:text-gray-600 rounded-r-md"
				>
					{open ? (
						<ChevronDown className="w-3 h-3" />
					) : (
						<ChevronRight className="w-3 h-3" />
					)}
				</button>
			</div>

			{open && (
				<div className="mt-0.5 space-y-0.5">
					{evalSubItems.map(item => {
						const active = isModuleActive(item.slug, location.pathname)
						return (
							<NavLink
								key={item.slug}
								to={item.to}
								onClick={onNav}
								className={() => subLinkClass(active)}
								data-tour={`nav-${item.slug}`}
							>
								<span className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
									{item.icon}
								</span>
								<span>{item.label}</span>
							</NavLink>
						)
					})}
				</div>
			)}
		</div>
	)
}

const AdminPanelCN = ({ mobileOpen, onMobileClose }: AdminPanelCNProps) => {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const location = useLocation()

	const { ids } = useSelector((state: any) => state.myPartners.myPendingInvitations)
	const hasPendingInvitations = ids.length > 0

	const { userInfo } = useSelector((state: any) => state.userLogin)

	// Mobile gets a deliberately short menu: Home, Locations, Projects,
	// Portfolios, Tasks, Partners. Admin tooling, the Evaluations group and
	// Explorer are desktop work. The User section is redundant on a phone
	// because HeaderCN's avatar dropdown already offers User Profile and Logout
	// at every viewport size — check that before assuming this strands anyone.
	const isMobile = useIsMobile()

	// Desktop/tablet only (MYD-532): the nav can shrink to a 64px icon rail.
	// Collapsed by default below 1280px, where the 146px it gives back matters
	// to the page; the user's choice, once made, is remembered.
	const [collapsed, setCollapsed] = usePersistedToggle('mdt.nav.collapsed', () => !viewportAtLeast(1280))

	const handleLogout = () => {
		dispatch(logout())
		// To a plain /login, not via the route guard, which would remember this
		// page for whoever signs in next (MYD-541).
		navigate(routes.LOGIN)
	}

	const handleNav = () => {
		if (onMobileClose) onMobileClose()
	}

	// Main section IA is user-locked (memory: feedback_sidebar_ia.md). Order:
	//   Home → Locations → Projects → Portfolios → Evaluations → Tasks → Explorer → Presentations → Partners
	// Dashboard, Templates (in Main), and the Admin Panel link are permanently
	// removed and must not be reintroduced. Evaluations is rendered separately
	// as a collapsible group (see EvaluationsNavGroup) — the items above it
	// appear before the group, the items below appear after.
	//
	// Revised 2026-09-04 by explicit user direction, superseding the earlier
	// "Main's order must not be added to" note:
	//   1. Chat is no longer a nav destination. The global chat is a modal
	//      popup opened from the Chat icon in the header
	//      (features/Chat/popup/ChatPopupTrigger). It is reachable from every
	//      screen, so a sidebar row for it is redundant. Do not re-add a Chat
	//      nav item, and do not restore the /chats route.
	//   2. Explorer moved out of ADMIN into Main, taking the slot Chat
	//      vacated. The older note that "Explorer sits beside Reports, not in
	//      Main" is retired. Repositioned on 2026-09-09, again by explicit user
	//      direction, to sit between Tasks and Partners rather than below
	//      Partners.
	//   3. Reports moved out of ADMIN into the Evaluations sub-group.
	//
	// Revised 2026-09-25 by explicit user direction (Presentations plan, Q2):
	//   4. Presentations joins Main directly after Explorer (MYD-494). It is
	//      hidden until REACT_APP_PRESENTATIONS_ENABLED is on, and on mobile,
	//      where the gallery is desktop-only.
	interface MainNavItem {
		to: string
		icon: React.ReactNode
		label: string
		end?: boolean
		badge?: boolean
		tourId?: string
		/**
		 * Highlight via isModuleActive(slug) instead of NavLink's own isActive.
		 * Explorer needs it: its links are grain-parameterised (/explorer/:grain),
		 * which NavLink's exact matching would not highlight.
		 */
		activeSlug?: string
	}

	const mainItemsBeforeEvaluations: MainNavItem[] = [
		// DESKTOP_HOME, not HOME: `/` goes through AppEntry, which on a phone
		// resolves to the field app, so a link here would bounce a user straight
		// back out of the site they had deliberately opened.
		{ to: routes.DESKTOP_HOME, icon: <Home className="w-5 h-5" />, label: 'Home', end: true },
		{ to: routes.LOCATIONS, icon: <MapPin className="w-5 h-5" />, label: 'Locations', tourId: 'nav-locations' },
		{ to: routes.PROJECTS, icon: <GitBranch className="w-5 h-5" />, label: 'Projects', tourId: 'nav-projects' },
		{ to: routes.PORTFOLIOS, icon: <FolderOpen className="w-5 h-5" />, label: 'Portfolios', tourId: 'nav-portfolios' },
	].filter(item => !(isMobile && isDesktopOnlyPath(item.to)))

	const mainItemsAfterEvaluations: MainNavItem[] = [
		{ to: routes.TASKSCN, icon: <ListTodo className="w-5 h-5" />, label: 'Tasks', tourId: 'nav-tasks' },
		{ to: routes.EXPLORER, icon: <Table2 className="w-5 h-5" />, label: 'Explorer', tourId: 'nav-explorer', activeSlug: 'explorer' },
		// Directly after Explorer, by explicit user direction on 2026-09-25
		// (MYD-494). activeSlug keeps it lit on /presentations/:id/edit and
		// /present too, which NavLink's own matching would not.
		...(isPresentationsEnabled()
			? [{ to: routes.PRESENTATIONS, icon: <Presentation className="w-5 h-5" />, label: 'Presentations', tourId: 'nav-presentations', activeSlug: 'presentations' }]
			: []),
		{ to: routes.PARTNERS, icon: <Users className="w-5 h-5" />, label: 'Partners', badge: hasPendingInvitations, tourId: 'nav-partners' },
		// Explorer drops out on mobile via the filter below — a wide data grid is
		// not phone work. The filter runs off the same registry the route guard
		// uses, so a visible link can never point at a blocked screen.
	].filter(item => !(isMobile && isDesktopOnlyPath(item.to)))

	const renderMainNavLink = (item: MainNavItem) => (
		<NavLink
			key={`${item.label}-${item.to}`}
			to={item.to}
			end={item.end}
			onClick={handleNav}
			className={({ isActive }) =>
				navLinkClass(
					item.activeSlug ? isModuleActive(item.activeSlug, location.pathname) : isActive,
				)
			}
			data-tour={item.tourId}
		>
			<span className="w-4 h-4 flex-shrink-0 flex items-center justify-center relative">
				{item.icon}
				{item.badge && (
					<span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-pink-500" />
				)}
			</span>
			<span>{item.label}</span>
		</NavLink>
	)

	// --- MOBILE OVERLAY ---
	const mobileOverlay = (
		<>
			{mobileOpen && (
				<div className="fixed inset-0 z-[9999] md:hidden">
					<div className="absolute inset-0 bg-black/30" onClick={onMobileClose} />
					<div className="absolute top-0 left-0 bottom-0 w-[210px] bg-white shadow-xl overflow-y-auto flex flex-col animate-in slide-in-from-left duration-200">
						{renderContent(onMobileClose)}
					</div>
				</div>
			)}
		</>
	)

	function renderContent(onCloseAction?: () => void) {
		return (
			<>
				{/* Header */}
				<div className="flex items-center justify-between px-4 h-14 border-b border-gray-200">
					<Link to={routes.DESKTOP_HOME}><img src={AvatarLogoFull} alt="MYDealTeams" className="w-[80px]" /></Link>
					{/* Desktop only: the mobile drawer has its own close button below. */}
					{!onCloseAction && (
						<button
							type="button"
							onClick={() => setCollapsed(true)}
							className="flex items-center justify-center w-8 h-8 rounded-md text-gray-400 hover:text-primary transition-colors"
							aria-label="Collapse navigation"
							title="Collapse navigation"
						>
							<PanelLeftClose className="w-4 h-4" />
						</button>
					)}
					{onCloseAction && (
						<button
							type="button"
							onClick={onCloseAction}
							className="flex items-center justify-center w-8 h-8 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
							aria-label="Close panel"
						>
							<X className="w-4 h-4" />
						</button>
					)}
				</div>

				{/* Main Navigation */}
				<div className="py-2">
					<p className={sectionLabelClass}>Main</p>
					<nav className="space-y-0.5">
						{mainItemsBeforeEvaluations.map(item => renderMainNavLink(item))}
						{/* A component rather than a data item, so the array filter above
						    cannot reach it — it needs a guard of its own. */}
						{!isMobile && <EvaluationsNavGroup onNav={handleNav} />}
						{mainItemsAfterEvaluations.map(item => renderMainNavLink(item))}
					</nav>
				</div>

				{/* Admin and User are desktop-only. Admin is workspace configuration
				    nobody does from a phone; User duplicates the header avatar
				    dropdown, which keeps User Profile and Logout reachable on every
				    viewport. Both blocks are hardcoded JSX rather than data, so they
				    take a guard here instead of the registry filter used on Main. */}
				{!isMobile && (
				<>
				<div className="border-t border-gray-200 my-1" />

				{/* Admin Navigation */}
				<div className="py-2">
					<p className={sectionLabelClass}>Admin</p>
					<nav className="space-y-0.5">
						{/* No top-level admin entries. Explorer moved to Main and
						    Reports moved into the Evaluations group on
						    2026-09-04 at the user's direction — see the IA note above
						    mainItemsBeforeEvaluations. The Admin Panel link is
						    permanently removed (memory: feedback_sidebar_ia.md). */}

						{/* Existing admin sub-collapsibles */}
						<CollapsibleSection label="Library" icon={<BookOpen className="w-4 h-4" />} pathPrefix={`${routes.ADMIN}/library`}>
							<NavLink
								to={`${routes.ADMIN}/library/documents`}
								onClick={handleNav}
								className={({ isActive }) => subLinkClass(isActive)}
							>
								Documents
							</NavLink>
							<NavLink
								to={`${routes.ADMIN}/library/images`}
								onClick={handleNav}
								className={({ isActive }) => subLinkClass(isActive)}
							>
								Images
							</NavLink>
						</CollapsibleSection>

						<CollapsibleSection label="Archives" icon={<Archive className="w-4 h-4" />} pathPrefix={`${routes.ADMIN}/archives`}>
							<NavLink
								to={`${routes.ADMIN}/archives/locations`}
								onClick={handleNav}
								className={({ isActive }) => subLinkClass(isActive)}
							>
								Locations
							</NavLink>
							<NavLink
								to={`${routes.ADMIN}/archives/portfolios`}
								onClick={handleNav}
								className={({ isActive }) => subLinkClass(isActive)}
							>
								Portfolios
							</NavLink>
							<NavLink
								to={`${routes.ADMIN}/archives/projects`}
								onClick={handleNav}
								className={({ isActive }) => subLinkClass(isActive)}
							>
								Projects
							</NavLink>
						</CollapsibleSection>

						<CollapsibleSection
							label="Templates"
							icon={<LayoutTemplate className="w-4 h-4" />}
							pathPrefix={[
								`${routes.ADMIN}/templates/tasks`,
								`${routes.ADMIN}/templates/sitedetails`,
								`${routes.ADMIN}/templates/scoring-criteria`,
								`${routes.ADMIN}/templates/acquisition`,
								`${routes.ADMIN}/templates/operating`,
								`${routes.ADMIN}/templates/roi-analysis`,
								`${routes.ADMIN}/templates/risks`,
								`${routes.ADMIN}/templates/pros-cons`,
							].join(',')}
						>
							<NavLink
								to={`${routes.ADMIN}/templates/tasks`}
								onClick={handleNav}
								className={({ isActive }) => subLinkClass(isActive)}
							>
								Tasks
							</NavLink>
							<NavLink
								to={`${routes.ADMIN}/templates/sitedetails`}
								onClick={handleNav}
								className={({ isActive }) => subLinkClass(isActive)}
							>
								Site Details
							</NavLink>
							<NavLink
								to={`${routes.ADMIN}/templates/scoring-criteria`}
								onClick={handleNav}
								className={({ isActive }) => subLinkClass(isActive)}
							>
								Scoring Criteria
							</NavLink>
							<NavLink
								to={`${routes.ADMIN}/templates/acquisition`}
								onClick={handleNav}
								className={({ isActive }) => subLinkClass(isActive)}
							>
								Acquisition
							</NavLink>
							<NavLink
								to={`${routes.ADMIN}/templates/operating`}
								onClick={handleNav}
								className={({ isActive }) => subLinkClass(isActive)}
							>
								Operating
							</NavLink>
							<NavLink
								to={`${routes.ADMIN}/templates/roi-analysis`}
								onClick={handleNav}
								className={({ isActive }) => subLinkClass(isActive)}
							>
								ROI Analysis
							</NavLink>
							<NavLink
								to={`${routes.ADMIN}/templates/risks`}
								onClick={handleNav}
								className={({ isActive }) => subLinkClass(isActive)}
							>
								Risks
							</NavLink>
							<NavLink
								to={`${routes.ADMIN}/templates/pros-cons`}
								onClick={handleNav}
								className={({ isActive }) => subLinkClass(isActive)}
							>
								Pros &amp; Cons
							</NavLink>
						</CollapsibleSection>

						<CollapsibleSection label="Utilities" icon={<Wrench className="w-4 h-4" />} pathPrefix={`${routes.ADMIN}/utilities/bulkload,${routes.ADMIN}/utilities/lookuplists,${routes.ADMIN}/utilities/cleanup,${routes.ADMIN}/utilities/ai-usage`}>
							<NavLink
								to={`${routes.ADMIN}/utilities/bulkload`}
								onClick={handleNav}
								className={({ isActive }) => subLinkClass(isActive)}
							>
								Bulk Load
							</NavLink>
							<NavLink
								to={`${routes.ADMIN}/utilities/lookuplists`}
								onClick={handleNav}
								className={({ isActive }) => subLinkClass(isActive)}
							>
								Lists
							</NavLink>
							<NavLink
								to={`${routes.ADMIN}/utilities/cleanup`}
								onClick={handleNav}
								className={({ isActive }) => subLinkClass(isActive)}
							>
								Cleanup
							</NavLink>
							{/* Every account's AI requests and costs: administrators only (MYD-630). */}
							{userInfo?.isAdmin && (
								<NavLink
									to={`${routes.ADMIN}/utilities/ai-usage`}
									onClick={handleNav}
									className={({ isActive }) => subLinkClass(isActive)}
								>
									AI Usage
								</NavLink>
							)}
						</CollapsibleSection>

						{/*
							Support. Intended to grow: Help Docs is the first entry, not
							the only one it is shaped for.

							The impersonation tool at routes.ADMIN_SUPPORT is deliberately
							NOT listed. It has never appeared in this panel, it needs a
							master password, and surfacing it beside a user-facing help
							document would be the wrong company for both of them.
						*/}
						<CollapsibleSection
							label="Support"
							icon={<LifeBuoy className="w-4 h-4" />}
							pathPrefix={routes.ADMIN_HELP_DOCS}
						>
							<NavLink
								to={routes.ADMIN_HELP_DOCS}
								onClick={handleNav}
								className={({ isActive }) => subLinkClass(isActive)}
							>
								Help Docs
							</NavLink>
						</CollapsibleSection>

						{/* A single page, so a direct link rather than a collapsible
						    (MYD-658). Added by explicit user direction. */}
						<NavLink
							to={routes.ADMIN_VENDORS}
							onClick={handleNav}
							className={({ isActive }) => navLinkClass(isActive)}
						>
							<Handshake className="w-4 h-4 flex-shrink-0" />
							<span>Vendors</span>
						</NavLink>
					</nav>
				</div>

				<div className="border-t border-gray-200 my-1" />

				{/* User Section */}
				<div className="py-2">
					<p className={sectionLabelClass}>User</p>
					{/* Who the session is signed in as. Above the profile link and
					    outside the nav, because it is a label rather than somewhere
					    to go. Both lines truncate: the panel is 210px wide and an
					    email will outrun it long before a name does. */}
					{userInfo && (userInfo.name || userInfo.email) && (
						<div className="px-4 pb-2">
							{userInfo.name && (
								<p className="text-xs font-medium text-gray-700 truncate" title={userInfo.name}>
									{userInfo.name}
								</p>
							)}
							{userInfo.email && (
								<p className="text-[11px] text-gray-500 truncate" title={userInfo.email}>
									{userInfo.email}
								</p>
							)}
						</div>
					)}
					<nav className="space-y-0.5">
						<NavLink
							to={`${routes.ADMIN}/userprofile/`}
							onClick={handleNav}
							className={({ isActive }) => navLinkClass(isActive)}
						>
							<Smile className="w-4 h-4 flex-shrink-0" />
							<span>User Profile</span>
						</NavLink>
						<button
							type="button"
							onClick={handleLogout}
							className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md mx-2 transition-colors w-[calc(100%-16px)] text-left"
						>
							<LogOut className="w-4 h-4 flex-shrink-0" />
							<span>Logout</span>
						</button>
					</nav>
				</div>
				</>
				)}
			</>
		)
	}

	/*
		The collapsed nav: the folder-only logo and one icon per destination.

		Main items are real links, so the rail is a working nav and not just a
		way back to the full one. The Admin sections are not: each is a group of
		sub-pages that needs the full width to list, so their icons expand the
		nav instead of guessing which sub-page was meant.
	*/
	const railLinkClass = (isActive: boolean) =>
		`relative flex items-center justify-center w-10 h-10 mx-auto rounded-md transition-colors no-underline ${
			isActive ? 'text-[#0080fe] bg-highlight' : 'text-gray-600 hover:bg-gray-100'
		}`

	const inEvalContext =
		location.pathname.startsWith(routes.EVALUATIONS) ||
		evalSubItems.some(item => isModuleActive(item.slug, location.pathname))

	const renderRailLink = (item: MainNavItem) => (
		<NavLink
			key={`${item.label}-${item.to}`}
			to={item.to}
			end={item.end}
			title={item.label}
			aria-label={item.label}
			className={({ isActive }) =>
				railLinkClass(item.activeSlug ? isModuleActive(item.activeSlug, location.pathname) : isActive)
			}
		>
			{item.icon}
			{item.badge && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-pink-500" />}
		</NavLink>
	)

	const adminRailItems = [
		{ label: 'Library', icon: <BookOpen className="w-5 h-5" /> },
		{ label: 'Archives', icon: <Archive className="w-5 h-5" /> },
		{ label: 'Templates', icon: <LayoutTemplate className="w-5 h-5" /> },
		{ label: 'Utilities', icon: <Wrench className="w-5 h-5" /> },
		{ label: 'Support', icon: <LifeBuoy className="w-5 h-5" /> },
	]

	const renderRail = () => (
		<>
			<div className="flex flex-col items-center gap-1 py-2 border-b border-gray-200">
				<Link to={routes.DESKTOP_HOME} title="Home" className="flex items-center justify-center h-10">
					<img src={FolderLogo} alt="MYDealTeams" className="w-9" />
				</Link>
				<button
					type="button"
					onClick={() => setCollapsed(false)}
					className="flex items-center justify-center w-8 h-8 rounded-md text-gray-400 hover:text-primary transition-colors"
					aria-label="Expand navigation"
					title="Expand navigation"
				>
					<PanelLeftOpen className="w-4 h-4" />
				</button>
			</div>

			{/* Same mobile rules as the full nav: the item arrays are already
			    filtered, and Evaluations, Admin and User take the same guard. */}
			<nav aria-label="Main" className="flex flex-col gap-0.5 py-2">
				{mainItemsBeforeEvaluations.map(renderRailLink)}
				{!isMobile && (
					<NavLink
						to={routes.EVALUATIONS}
						title="Evaluations"
						aria-label="Evaluations"
						className={() => railLinkClass(inEvalContext)}
					>
						<ClipboardCheck className="w-5 h-5" />
					</NavLink>
				)}
				{mainItemsAfterEvaluations.map(renderRailLink)}
			</nav>

			{!isMobile && (
			<>
			<div className="border-t border-gray-200 my-1" />

			<nav aria-label="Admin" className="flex flex-col gap-0.5 py-2">
				{adminRailItems.map(item => (
					<button
						key={item.label}
						type="button"
						onClick={() => setCollapsed(false)}
						title={`${item.label} (expands the menu)`}
						aria-label={item.label}
						className={railLinkClass(false)}
					>
						{item.icon}
					</button>
				))}
				{/* One page rather than a group, so there is nothing to expand for. */}
				<NavLink
					to={routes.ADMIN_VENDORS}
					title="Vendors"
					aria-label="Vendors"
					className={({ isActive }) => railLinkClass(isActive)}
				>
					<Handshake className="w-5 h-5" />
				</NavLink>
			</nav>

			<div className="border-t border-gray-200 my-1" />

			<nav aria-label="User" className="flex flex-col gap-0.5 py-2">
				<NavLink
					to={`${routes.ADMIN}/userprofile/`}
					title="User Profile"
					aria-label="User Profile"
					className={({ isActive }) => railLinkClass(isActive)}
				>
					<Smile className="w-5 h-5" />
				</NavLink>
				<button
					type="button"
					onClick={handleLogout}
					title="Logout"
					aria-label="Logout"
					className={railLinkClass(false)}
				>
					<LogOut className="w-5 h-5" />
				</button>
			</nav>
			</>
			)}
		</>
	)

	return (
		<>
			{/* Mobile overlay */}
			{mobileOverlay}

			{/* Desktop/Tablet sidebar: full, or the icon rail when collapsed. */}
			<aside
				className={`hidden md:flex flex-col ${
					collapsed ? 'w-16' : 'w-[210px]'
				} bg-white border-r border-gray-200 flex-shrink-0 overflow-y-auto overflow-x-hidden`}
			>
				{collapsed ? renderRail() : renderContent()}
			</aside>
		</>
	)
}

export default AdminPanelCN
